import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  DropDown,
  DynamicFormRequiredType,
  HfInput,
  HfToggleButton,
  type JsonData,
} from '@myosh/odin-components';
import cx from 'classnames';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useRef, useState } from 'react';
import { Controller, type FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import { useAuth } from '../context/auth.context';
import { UserActionTypes, useUser } from '../context/user.context';
import { checkUserExists } from '../helpers/check-user-exists';
import { getSchemas } from '../helpers/get-schemas';
import Footer from './footer.component';
import type { LoginResult } from './login.component';
import Sidebar from './sidebar.component';

interface SchemaDetails {
  text: string;
  value: string;
}

const schema = yup.object().shape({
  username: yup.string().when('toggle', {
    is: true,
    then: (schema) => schema.required('Username is a required field'),
    otherwise: (schema) => schema.nullable(),
  }),
});

export interface LoginSchemaProps {
  idToken: string;
  onSchemaSelected: (schema: string) => void;
}

export default function LoginSchema({ idToken, onSchemaSelected }: LoginSchemaProps) {
  const isSuperUser = useRef(false);
  const hasRedirectSchema = useRef(false);
  const [availableSchemas, setAvailableSchemas] = useState<Array<SchemaDetails>>();
  const [schemas, setSchemas] = useState<Array<SchemaDetails>>([]);
  const [usernameInputVisible, setUsernameInputVisible] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
  } = useForm<FieldValues>({ resolver: yupResolver(schema) as never, mode: 'onSubmit', reValidateMode: 'onSubmit' });
  const { dispatch, state } = useUser();
  const { logout } = useAuth();

  // biome-ignore lint/correctness/useExhaustiveDependencies: dispatch and logout are intentionally omitted as they are stable from context and including them would not provide value and could cause unnecessary re-renders
  useEffect(() => {
    if (idToken && idToken.length > 0) {
      const decodedToken: Record<string, string> = jwtDecode(idToken);
      const superUser = decodedToken['custom:superuser'];

      if (superUser && superUser.toLocaleLowerCase() === 'true') {
        isSuperUser.current = true;
      }
      /**
       * We need to check for schema parameter in the redirectQueryParams that might be passed to the Login component and then set that schema instead of fetching any.
       */
      if (localStorage.getItem('redirectQueryParams')) {
        const redirectQueryParams = localStorage.getItem('redirectQueryParams');
        if (typeof redirectQueryParams === 'string') {
          const redirectQuery = JSON.parse(redirectQueryParams);
          if (redirectQuery.schema) {
            hasRedirectSchema.current = true;
            setSchemas([{ text: redirectQuery.schema, value: redirectQuery.schema }]);
          }
        }
      }

      if (!hasRedirectSchema.current) {
        const tokenSchemas = decodedToken['custom:schema'].split(',');

        if (!isSuperUser.current && tokenSchemas && tokenSchemas.length === 1 && tokenSchemas[0].length > 0) {
          // if the user is not a super user and there is only one schema in the token,
          // we set it as the only schema available,
          // which will automatically select it in the dropdown and redirect to the next page
          setSchemas([{ text: tokenSchemas[0], value: tokenSchemas[0] }]);
        } else {
          // in every other case, i.e. if there are multiple schemas in the token, or if the user is a superuser,
          // we need to fetch all schemas available for the user.
          getSchemas(idToken)
            .then((resp) => {
              const formattedSchemas = (resp.result ?? [])
                .sort((schema1, schema2) => schema1.name.localeCompare(schema2.name))
                .map((schema) => ({
                  text: isSuperUser.current ? `${schema.name} (${schema.code})` : schema.name,
                  value: schema.code,
                }));

              setSchemas(formattedSchemas);

              const schemaCodes = formattedSchemas.map((schema) => schema.value);
              dispatch({ type: UserActionTypes.UpdateSchemas, payload: schemaCodes.join(',') });
            })
            .catch(() => {
              dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'login' });
              logout();
            });
        }
      }
    }
  }, [idToken]);

  // - dispatch is stable from context
  // - onSchemaSelected is from props and would cause re-redirects if included
  // - setAvailableSchemas is a local setState function (stable)
  // This effect should only run when the schemas array changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: dispatch, onSchemaSelected, and setAvailableSchemas are intentionally omitted. See above.
  useEffect(() => {
    if (schemas.length === 1) {
      setTimeout(() => onSchemaSelected(schemas[0].value)); // allows for state to be persisted before redirecting (MYOSH-8225)
      dispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: schemas[0].value });
      dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'done' });
    }

    if (schemas.length > 1) {
      setAvailableSchemas(schemas);
    }
  }, [schemas]);

  const onSubmit = async (data: FieldValues) => {
    dispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: data.schema.value });

    if (isSuperUser.current && usernameInputVisible && data.username) {
      const userExists = await checkUserExists(idToken, data.schema.value, data.username);
      if (!userExists) {
        setError('username', { message: 'User not found in the selected schema' });
        return;
      }
      dispatch({
        type: UserActionTypes.UpdateControlledUsername,
        payload: data.username,
      });
    }

    setTimeout(() => onSchemaSelected(data.schema.value)); // allows for state to be persisted before redirecting (MYOSH-8225)
    dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'done' });
  };

  const handleUsernameInputVisibleToggle = () => {
    setUsernameInputVisible(!usernameInputVisible);
    clearErrors();
  };

  const usernameInputContainer = cx(
    'mt-2 h-[50px]',
    { visible: usernameInputVisible },
    { invisible: !usernameInputVisible }
  );

  return (
    <div className="myl-login-layout flex h-full min-h-screen w-full flex-col sm:flex-row">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col bg-mono-1">
        <div className="flex max-w-sm flex-1 flex-col justify-center px-8 pb-12 md:ml-24 md:px-0">
          <h2 className="mt-8 mb-6 text-4xl font-bold sm:mt-12">Welcome {state.user?.preferred_username} </h2>
          <p className="mb-4 sm:text-xl md:text-2xl">Select {isSuperUser.current ? 'schema' : 'company'} to continue</p>
          <form data-testid="myl-select-schema-form" onSubmit={handleSubmit(onSubmit)}>
            {availableSchemas && availableSchemas.length > 0 && (
              <>
                <Controller
                  control={control}
                  name={'schema'}
                  defaultValue={availableSchemas[0]}
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                      <DropDown
                        selectorStyles="text-sm"
                        data={availableSchemas as unknown as JsonData}
                        textField="text"
                        valueField="value"
                        onChange={onChange}
                        value={value}
                        error={error?.message}
                        allowClear={false}
                        allowSearch={true}
                      />
                    );
                  }}
                />

                {isSuperUser.current && (
                  <div className="mt-4">
                    <div className="flex">
                      <HfToggleButton
                        control={control}
                        name="toggle"
                        checked={usernameInputVisible}
                        onChange={handleUsernameInputVisibleToggle}
                        className="flex"
                      />
                      <div className="ml-2 flex items-center text-sm">Control a user in the above schema?</div>
                    </div>
                    <div className={usernameInputContainer}>
                      <HfInput
                        control={control}
                        name="username"
                        type="text"
                        placeholder="Enter username"
                        required={DynamicFormRequiredType.True}
                        error={String(errors.username?.message)}
                        enableDebounce={false}
                      />
                    </div>
                  </div>
                )}

                <Button type="primary" htmlType="submit" classNames="mt-4">
                  Select {isSuperUser.current ? 'Schema' : 'Company'}
                </Button>
              </>
            )}
          </form>
        </div>
        <Footer />
      </div>
    </div>
  );
}

// A convenience hook that provides the schema selection callback
export const useLoginSchema = (redirectPath?: string, onLoginSuccess?: (result: LoginResult) => void) => {
  const navigate = useNavigate();
  const { state } = useUser();
  const tokens = state.tokens;

  const onSchemaSelected = async (schema: string) => {
    // redirect after success
    if (redirectPath) {
      const redirectQueryParams = localStorage.getItem('redirectQueryParams');

      // add query params to redirect
      if (redirectQueryParams) {
        const queryParams = new URLSearchParams(JSON.parse(redirectQueryParams)).toString();

        return navigate(
          {
            pathname: redirectPath,
            search: `?${queryParams}`,
          },
          { replace: true }
        );
      }
      return navigate(
        {
          pathname: redirectPath,
        },
        { replace: true }
      );
    }
    // data for manual token storage and event trigger for a successful login
    if (onLoginSuccess) {
      onLoginSuccess({
        accessToken: tokens?.access_token || '',
        idToken: tokens?.id_token || '',
        refreshToken: tokens?.refresh_token || '',
        selectedSchema: schema,
      });
    }
  };

  return { onSchemaSelected };
};
